// Merge ayah/surah/page bookmark maps from two sources (prefer newer timestamp per key).
function mergeBookmarkPayloads(a, b) {
  const merge = (aObj, bObj) => {
    const out = { ...(aObj || {}) };
    for (const [k, v] of Object.entries(bObj || {})) {
      if (!out[k]) {
        out[k] = v;
        continue;
      }
      const ta = out[k].timestamp;
      const tb = v.timestamp;
      if (tb != null && (ta == null || tb > ta)) out[k] = v;
    }
    return out;
  };
  return {
    surahs: merge(a?.surahs, b?.surahs),
    ayahs: merge(a?.ayahs, b?.ayahs),
    pages: merge(a?.pages, b?.pages),
  };
}

const MIGRATION_KEY = 'storageMigrated_v2';

function isWordStorageKey(key) {
  return /^qdv\d+:words:/.test(key) || key.startsWith('words:') || key.includes(':words:');
}

// Storage Manager with quota handling and fallbacks
class StorageManager {
  constructor() {
    this.storageKey = 'quran_app_data';
    this.maxSize = 8 * 1024 * 1024; // Soft budget; browsers often cap closer to ~5MB
    this.cleanupThreshold = 0.8; // Start cleanup when 80% full
    this.lastCleanupTime = 0;
    this.cleanupCooldown = 5000; // 5 seconds between cleanup logs
    // One-time + every-boot purge of legacy word caches that filled quota
    this.runStaleStorageMigration();
  }

  /**
   * Drop stale word-by-word localStorage blobs and (once) other heavy API caches.
   * Preserves bookmarks, theme, reciter, reading position, and similar prefs.
   */
  runStaleStorageMigration() {
    if (typeof localStorage === 'undefined') return;
    try {
      const before = this.getStorageUsage();
      this.removeWordData();

      let migrated = false;
      try {
        migrated = localStorage.getItem(MIGRATION_KEY) === '1';
      } catch {
        migrated = false;
      }

      const usage = this.getStorageUsage();
      const stillTight = usage > this.maxSize * 0.55;

      if (!migrated || stillTight) {
        this.clearRukuData();
        this.clearSurahData();
        this.clear(); // other qdv / mirror caches — not user prefs
        this.removeWordData();
      }

      try {
        localStorage.setItem(MIGRATION_KEY, '1');
      } catch {
        try {
          this.removeWordData();
          this.clearRukuData();
          localStorage.setItem(MIGRATION_KEY, '1');
        } catch {
          try {
            sessionStorage.setItem(MIGRATION_KEY, '1');
          } catch {
            /* ignore */
          }
        }
      }

      const after = this.getStorageUsage();
      if (before - after > 50 * 1024) {
        console.log(
          `[storage] Freed ~${Math.round((before - after) / 1024)}KB of stale cache`
        );
      }
    } catch {
      /* migration must never break app boot */
    }
  }

  /**
   * Manual / Settings: free downloadable caches (words, rukus, surah payloads).
   * Does not remove bookmarks or user preferences.
   * @returns {{ freedBytes: number, usageBefore: number, usageAfter: number }}
   */
  freeCacheSpace() {
    const usageBefore = this.getStorageUsage();
    this.removeWordData();
    this.clearRukuData();
    this.clearSurahData();
    this.clear();
    this.removeWordData();
    try {
      localStorage.setItem(MIGRATION_KEY, '1');
    } catch {
      /* ignore */
    }
    const usageAfter = this.getStorageUsage();
    return {
      freedBytes: Math.max(0, usageBefore - usageAfter),
      usageBefore,
      usageAfter,
    };
  }

  // Check if localStorage is available and has space
  isStorageAvailable() {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  // Get storage usage statistics
  getStorageUsage() {
    try {
      let totalSize = 0;
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        try {
          totalSize += localStorage[key].length + key.length;
        } catch (e) {
          console.warn('Failed to calculate size for key:', key, e);
        }
      });
      
      return totalSize;
    } catch (error) {
      console.error('Storage usage calculation failed:', error);
      return 0;
    }
  }

  // Check storage health and provide recommendations
  getStorageHealth() {
    const usage = this.getStorageUsage();
    const percentage = (usage / this.maxSize) * 100;
    
    if (percentage >= 90) {
      return {
        status: 'critical',
        message: 'Storage is nearly full. Some features may not work properly.',
        recommendation: 'Clear some data or refresh the page to free space.',
        percentage: Math.round(percentage)
      };
    } else if (percentage >= 75) {
      return {
        status: 'warning',
        message: 'Storage usage is high.',
        recommendation: 'Consider clearing old data to prevent issues.',
        percentage: Math.round(percentage)
      };
    } else {
      return {
        status: 'healthy',
        message: 'Storage usage is normal.',
        recommendation: 'No action needed.',
        percentage: Math.round(percentage)
      };
    }
  }

  // Safe set item with size checking
  setItem(key, value) {
    try {
      const dataSize = JSON.stringify(value).length;
      const currentUsage = this.getStorageUsage();
      
      // Special handling for bookmarks - ensure they're always saved
      if (key === 'bookmarks') {
        // If localStorage is full, do aggressive cleanup to make room for bookmarks
        if (currentUsage + dataSize > this.maxSize) {
          console.log('Storage full, doing aggressive cleanup to save bookmarks');
          this.cleanupStorage(true); // Force aggressive cleanup
          
          // If still full, try to free more space by removing word data
          if (this.getStorageUsage() + dataSize > this.maxSize) {
            this.removeWordData();
          }
        }
        
        // Now try to save bookmarks
        if (this.getStorageUsage() + dataSize <= this.maxSize) {
          localStorage.setItem(key, JSON.stringify(value));
          sessionStorage.removeItem(key);
          return true;
        } else {
          // Last resort: use sessionStorage for bookmarks
          console.warn('Using sessionStorage for bookmarks due to quota');
          sessionStorage.setItem(key, JSON.stringify(value));
          localStorage.removeItem(key);
          return true;
        }
      }
      
      // Check if we're approaching quota and do proactive cleanup
      if (currentUsage > this.maxSize * this.cleanupThreshold) {
        const now = Date.now();
        if (now - this.lastCleanupTime > this.cleanupCooldown) {
          console.log(`Storage usage high (${Math.round(currentUsage / 1024 / 1024 * 100) / 100}MB), doing proactive cleanup`);
          this.lastCleanupTime = now;
        }
        this.cleanupStorage();
      }
      
      // Check if adding this data would exceed quota
      if (currentUsage + dataSize > this.maxSize) {
        const now = Date.now();
        if (now - this.lastCleanupTime > this.cleanupCooldown) {
          console.warn(`Storage quota would be exceeded. Current: ${currentUsage}, Adding: ${dataSize}, Limit: ${this.maxSize}`);
          this.lastCleanupTime = now;
        }
        this.cleanupStorage();
        
        // Try again after cleanup
        if (this.getStorageUsage() + dataSize <= this.maxSize) {
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } else {
          // Still too large, use sessionStorage as fallback
          console.warn('Using sessionStorage as fallback for large data');
          sessionStorage.setItem(key, JSON.stringify(value));
          return true;
        }
      }
      
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      // Quota full — wipe heavy qdv/word caches and retry once (avoid spam)
      if (
        error &&
        (error.name === 'QuotaExceededError' ||
          /quota/i.test(String(error.message || '')))
      ) {
        try {
          this.cleanupStorage(true);
          this.removeWordData();
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch {
          /* fall through — sessionStorage / fail quietly (no spam) */
        }
      } else {
        console.error('Storage error:', error);
      }

      // Fallback to sessionStorage
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (fallbackError) {
        console.error('SessionStorage also failed for', key, fallbackError);
        return false;
      }
    }
  }

  // Safe get item with fallback
  getItem(key) {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('localStorage get error:', error);
      
      // Try sessionStorage as fallback
      try {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
      } catch (fallbackError) {
        console.error('sessionStorage get error:', fallbackError);
        return null;
      }
    }
  }

  // Special getter for bookmarks that checks both storages
  getBookmarks() {
    try {
      const loc = localStorage.getItem('bookmarks');
      const sess = sessionStorage.getItem('bookmarks');
      const parsedLoc = loc ? JSON.parse(loc) : null;
      const parsedSess = sess ? JSON.parse(sess) : null;
      if (!parsedLoc && !parsedSess) return null;
      if (!parsedLoc) return parsedSess;
      if (!parsedSess) return parsedLoc;
      // Both exist: merge so newer ayah/surah/page entries are not hidden (local-first read was losing session-only updates).
      return mergeBookmarkPayloads(parsedLoc, parsedSess);
    } catch (error) {
      console.error('Bookmark retrieval error:', error);
      return null;
    }
  }

  // Cleanup old data to free space
  cleanupStorage(force = false) {
    try {
      // Browser quota (~5MB) is often lower than our soft maxSize — on hard
      // quota failure always drop word caches first regardless of estimated usage.
      if (force) {
        this.removeWordData();
      }

      const keys = Object.keys(localStorage);
      
      // Prioritize cleanup: word-by-word singleFlight, ruku SF, surah SF, then other qdv mirrors
      const wordKeys = keys.filter(isWordStorageKey);
      const rukuKeys = keys.filter(
        (key) => /^qdv\d+:rukus:/.test(key) || key.startsWith('rukus:')
      );
      const surahKeys = keys.filter(
        (key) => /^qdv\d+:surah:/.test(key) || key.startsWith('surah:')
      );
      const mirrorKeys = keys.filter((key) =>
        /^qdv\d+:(surahs|parahsData|surahData-|rukusData-|ayahs_)/.test(key)
      );
      
      // Calculate how much space we need to free
      const currentUsage = this.getStorageUsage();
      const targetUsage = force ? this.maxSize * 0.3 : this.maxSize * 0.6; // More aggressive cleanup for bookmarks
      const spaceToFree = currentUsage - targetUsage;
      
      if (spaceToFree <= 0 && !force) return;
      if (spaceToFree <= 0) {
        // Still remove ruku/surah/mirrors on force so small prefs can write
        [...rukuKeys, ...surahKeys, ...mirrorKeys].forEach((key) => {
          try {
            localStorage.removeItem(key);
          } catch {
            /* ignore */
          }
        });
        return;
      }
      
      let freedSpace = 0;
      const keysToRemove = [];
      
      // Remove old word-by-word data first (most space-consuming)
      for (const key of wordKeys) {
        try {
          const size = localStorage[key].length + key.length;
          keysToRemove.push({ key, size });
          freedSpace += size;
          if (freedSpace >= spaceToFree) break;
        } catch (e) {
          console.warn('Failed to calculate size for key:', key, e);
        }
      }
      
      // If we still need more space, remove old ruku data
      if (freedSpace < spaceToFree) {
        for (const key of rukuKeys) {
          try {
            const size = localStorage[key].length + key.length;
            keysToRemove.push({ key, size });
            freedSpace += size;
            if (freedSpace >= spaceToFree) break;
          } catch (e) {
            console.warn('Failed to calculate size for key:', key, e);
          }
        }
      }
      
      // If we still need more space, remove old surah data
      if (freedSpace < spaceToFree) {
        for (const key of surahKeys) {
          try {
            const size = localStorage[key].length + key.length;
            keysToRemove.push({ key, size });
            freedSpace += size;
            if (freedSpace >= spaceToFree) break;
          } catch (e) {
            console.warn('Failed to calculate size for key:', key, e);
          }
        }
      }

      // Surah list / parahs / component mirrors (qdv-prefixed)
      if (freedSpace < spaceToFree) {
        for (const key of mirrorKeys) {
          try {
            const size = localStorage[key].length + key.length;
            keysToRemove.push({ key, size });
            freedSpace += size;
            if (freedSpace >= spaceToFree) break;
          } catch (e) {
            console.warn('Failed to calculate size for key:', key, e);
          }
        }
      }
      
      // Remove the selected keys
      keysToRemove.forEach(({ key }) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn('Failed to remove key:', key, e);
        }
      });
      
      console.log(`Cleaned up ${keysToRemove.length} old storage items, freed ${Math.round(freedSpace / 1024)}KB`);
    } catch (error) {
      console.error('Cleanup failed:', error);
    }
  }

  // Aggressive word data removal for bookmark storage
  removeWordData() {
    try {
      const keys = Object.keys(localStorage);
      const wordKeys = keys.filter(isWordStorageKey);

      wordKeys.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      });
    } catch {
      /* silent — cleanup must not spam console */
    }
  }

  // Remove specific item
  removeItem(key) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Remove item error:', error);
    }
  }

  // Clear all app data
  clear() {
    try {
      const keys = Object.keys(localStorage);
      let n = 0;
      keys.forEach((key) => {
        if (key === MIGRATION_KEY) return;
        if (
          key.startsWith('qdv') ||
          key.startsWith('surah:') ||
          key.startsWith('rukus:') ||
          key.startsWith('words:') ||
          key === 'surahs' ||
          key === 'parahsData' ||
          key.startsWith('surahData-') ||
          key.startsWith('rukusData-') ||
          key.startsWith('ayahs_')
        ) {
          localStorage.removeItem(key);
          n += 1;
        }
      });
      if (n > 0) {
        console.log(`[storage] Cleared ${n} cache keys`);
      }
    } catch (error) {
      console.error('Clear failed:', error);
    }
  }

  // Clear specific data types
  clearWordData() {
    this.removeWordData();
  }

  clearRukuData() {
    try {
      const keys = Object.keys(localStorage);
      const rukuKeys = keys.filter(
        (key) =>
          /^qdv\d+:rukus:/.test(key) ||
          /^qdv\d+:rukusData-/.test(key) ||
          key.startsWith('rukus:') ||
          key.startsWith('rukusData-')
      );
      rukuKeys.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error('Ruku data clear failed:', error);
    }
  }

  clearSurahData() {
    try {
      const keys = Object.keys(localStorage);
      const surahKeys = keys.filter(
        (key) =>
          /^qdv\d+:surah:/.test(key) ||
          /^qdv\d+:surahData-/.test(key) ||
          /^qdv\d+:surahs$/.test(key) ||
          key.startsWith('surah:') ||
          key.startsWith('surahData-') ||
          key === 'surahs'
      );
      surahKeys.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error('Surah data clear failed:', error);
    }
  }
}

export const storageManager = new StorageManager();
